/**
 * Unified placement validation.
 * Single function that checks all collision types for any fixture.
 *
 * Pure computation — no Firebase calls.
 */

const { getClearanceConfig } = require('./clearanceConfig');
const { checkFixtureCollisions } = require('./fixtureCollision');
const { checkDoorSwingCollisions } = require('./doorCollision');
const {
  checkWallMountedCollisions,
  checkParentWallBounds,
} = require('./wallCollision');
const { checkClearanceBoundary } = require('./clearanceBoundary');
const { checkPhysicalBoundary } = require('./physicalBoundary');

/**
 * Check if a fixture placement is valid.
 * Runs all applicable collision checks based on fixture type.
 *
 * @param {Object} object - The fixture being placed
 *   { id, type, center: {x,y,z}, dimensions: {x,y,z}, rotation: {y},
 *     parentWallIds?: string[], fixtureGroupId?: string, category?: {} }
 * @param {Object} roomData - Room data
 *   { walls, doors, windows, openings, objects, floors }
 * @param {Object} [options]
 * @param {string[]} [options.excludeIds] - Object IDs to skip in fixture-to-fixture
 * @param {string[]} [options.excludeGroupIds] - fixtureGroupIds to skip
 * @param {string[]} [options.skipChecks] - Check types to skip, e.g. ['door-swing', 'clearance-boundary']
 * @param {Object} [options.snappedWall] - Wall the object is currently snapped to (overrides parentWallIds)
 * @param {Map<string, Object>} [options.structureDoorsById] - Optional
 *   structure-door lookup. Forwarded to checkDoorSwingCollisions so
 *   doorType / wallFacesPriorityRoom / coords come from the canonical
 *   structure-level copy (P3a-ready). Falls back to room-side reads.
 * @returns {{ valid: boolean, collisions: Array<{type, target, reason}> }}
 */
function checkPlacement(object, roomData, options = {}) {
  const {
    excludeIds = [],
    excludeGroupIds = [],
    skipChecks = [],
    structureDoorsById,
  } = options;
  const config = getClearanceConfig(object);
  const collisions = [];

  if (!object.center || !object.dimensions) {
    return { valid: true, collisions: [] };
  }

  // Skip entirely for objects that don't participate in collision (e.g., countertops)
  if (config.skipCollisionCheck) {
    return { valid: true, collisions: [] };
  }

  const skip = new Set(skipChecks);

  // Resolve the wall this object is attached to — prefer snappedWall, fall back to parentWallIds
  const attachedWall =
    options.snappedWall ||
    (object.parentWallIds?.[0]
      ? (roomData.walls || []).find((w) => w.id === object.parentWallIds[0])
      : null);

  // --- Wall-mounted fixtures: delegate to specialized checks ---
  if (config.isWallMounted) {
    if (!skip.has('parent-wall-bounds') && attachedWall) {
      collisions.push(
        ...checkWallMountedCollisions(object, roomData, attachedWall)
      );
    }
    return { valid: collisions.length === 0, collisions };
  }

  // --- Floor-level fixtures: run all applicable checks ---

  // 1. Fixture-to-fixture physical overlap
  if (!skip.has('fixture-to-fixture')) {
    collisions.push(
      ...checkFixtureCollisions(object, roomData.objects || [], {
        excludeIds,
        excludeGroupIds,
      })
    );
  }

  // 2. Door swing overlap
  if (!skip.has('door-swing') && config.checkDoorSwing) {
    collisions.push(
      ...checkDoorSwingCollisions(object, roomData, structureDoorsById)
    );
  }

  // 3. Wall bounds — temporarily disabled. The bathroom-V2 pipeline doesn't
  // honor this check (locked fixtures with overshoot pass through unflagged),
  // and there's no fix flow yet, so surfacing it produces collisions that
  // generate_fix_candidates can't resolve. Re-enable once the pipeline
  // enforces it or a dedicated resolver exists.
  // The `false` below is that disable switch, not a mistake — remove it to
  // re-enable. The directive must stay a single line: a `-next-line`
  // comment applies to the line after it, so wrapping it silences nothing.
  // eslint-disable-next-line no-constant-condition
  if (false && !skip.has('parent-wall-bounds') && attachedWall) {
    collisions.push(
      ...checkParentWallBounds(object, roomData.walls || [], attachedWall)
    );
  }

  // 4. Clearance zone vs non-parent walls (each wall as outward-extruded OBB).
  // Cabinets carry per-sink footprints with frontClearance: 0 in their config,
  // so we let checkClearanceBoundary self-gate on footprint count rather than
  // gating here on the cabinet's own frontClearance.
  const isCabinet = (object.type || '').toLowerCase() === 'lower-cabinet';
  if (
    !skip.has('clearance-boundary') &&
    (config.frontClearance > 0 || isCabinet)
  ) {
    collisions.push(...checkClearanceBoundary(object, roomData, attachedWall));
  }

  // 5. Physical body vs non-parent walls (each wall as outward-extruded OBB)
  if (!skip.has('physical-boundary')) {
    collisions.push(...checkPhysicalBoundary(object, roomData, attachedWall));
  }

  return { valid: collisions.length === 0, collisions };
}

module.exports = { checkPlacement };
